import { SetupStep } from '@modules/WalletSetup/components/SetupStep'
import { unlock } from '@plug-core/handlers/walletActions/wallet'
import { logger } from '@plug-core/logger'
import { useCurrentWallet, usePendingRequests, useWalletProperties } from '@plug-core/state'
import { Button, Dialog, Input, Text } from '@ui'
import { disconnect } from '@utils/disconnect'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { HiMiniExclamationTriangle } from 'react-icons/hi2'

export const WalletUnlock = () => {
  const { pendingRequests, removePendingRequest } = usePendingRequests()
  const { currentWallet } = useCurrentWallet()
  const { walletPlugbookRecords } = useWalletProperties()

  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState(false)
  const [showWalletResetConfirmation, setShowWalletResetConfirmation] = useState(false)

  const { t } = useTranslation()

  const performUnlock = async (event: React.SyntheticEvent<HTMLFormElement>) => {
    event.preventDefault()
    try {
      setIsLoading(true)

      let result = await unlock(password)
      if (result === false) {
        setError(true)
        return
      }

      const unlockRequests = pendingRequests.filter(({ method }) => method === 'unlock')

      if (unlockRequests.length) {
        unlockRequests.forEach((req) => {
          removePendingRequest(req.id)
        })

        const hasOtherRequests = pendingRequests.some(({ method }) => method !== 'unlock')

        if (!hasOtherRequests) {
          const currentWindow = await chrome.windows.getCurrent()
          chrome.windows.remove(currentWindow.id!)
        }
      }
    } catch (error) {
      logger.debug('Failed to unlock wallet', { originalError: error })
    } finally {
      setIsLoading(false)
    }
  }

  const walletName = walletPlugbookRecords?.[currentWallet || '']?.name

  return (
    <SetupStep
      title="Welcome back"
      description={
        walletName ? (
          <Text as="span" variant="body" weight="semibold" tone="fg-2">
            @{walletName}
          </Text>
        ) : (
          'Plug into Crypto'
        )
      }
    >
      <form onSubmit={performUnlock} className="flex flex-col gap-4">
        <Input
          type="password"
          placeholder="Enter password"
          aria-label="Password"
          disabled={isLoading}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          error={error}
          autoComplete="off"
          autoFocus
        />
        <Button
          type="submit"
          variant="primary"
          fullWidth
          loading={isLoading}
          disabled={password.length === 0}
        >
          {t('unlock.title')}
        </Button>
        <Button
          variant="link"
          className="self-center text-[14px]"
          onClick={() => setShowWalletResetConfirmation(true)}
        >
          {t('unlock.moreOptions')}
        </Button>
      </form>

      <Dialog
        open={!!showWalletResetConfirmation}
        dismissible={false}
        icon={
          <span className="grid size-[50px] place-items-center rounded-full bg-surface-2 text-[30px] text-warning">
            <HiMiniExclamationTriangle aria-hidden="true" />
          </span>
        }
        title={t('unlock.logoutWarning.title')}
        description={t('unlock.logoutWarning.description')}
        actions={
          <>
            <Button fullWidth onClick={disconnect} variant="danger">
              {t('unlock.logoutWarning.confirm')}
            </Button>
            <Button
              fullWidth
              onClick={() => setShowWalletResetConfirmation(false)}
              variant="secondary"
            >
              {t('unlock.logoutWarning.cancel')}
            </Button>
          </>
        }
      />
    </SetupStep>
  )
}
